import React, { useEffect, useRef, useState } from 'react';
import { Download, Loader2, Film, Video } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import GIF from 'gif.js.optimized';
import QRCode from 'qrcode';
import FlyerCircular from '@/components/flyer/FlyerCircular';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_HERO =
  'https://www.casasamba.com/wp-content/uploads/2022/03/NASPA-Parade-high-3.gif';

/**
 * PUBLIC flyer download page. Public visitors just see the finished flyer +
 * big Download buttons. Admins get an inline edit toolbar (bottom-right) that
 * lets them rewrite every text label on the flyer.
 */
const FlyerPublic = () => {
  return (
    <PageEditProvider slug="flyer">
      <FlyerPublicInner />
      <PageEditToolbar />
    </PageEditProvider>
  );
};

const FlyerPublicInner = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const flyerRef = useRef(null);

  const loadClasses = async () => {
    try {
      const res = await axios.get(`${API}/api/classes`);
      setClasses(Array.isArray(res.data) ? res.data.filter((c) => c.is_active !== false) : []);
    } catch {
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClasses(); }, []);

  const exportImage = async (format) => {
    if (!flyerRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(flyerRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `casa-samba-classes-flyer.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } else {
        const imgData = canvas.toDataURL('image/png');
        // Match the new circular flyer aspect ratio (1300x1820)
        const pdf = new jsPDF({ unit: 'px', format: [1300, 1820], orientation: 'portrait' });
        pdf.addImage(imgData, 'PNG', 0, 0, 1300, 1820);
        pdf.save(`casa-samba-classes-flyer.pdf`);
      }
      toast.success(`Flyer downloaded as ${format.toUpperCase()}`);
    } catch (err) {
      console.error(err);
      toast.error('Download failed — try again or use a different browser');
    } finally {
      setExporting(false);
    }
  };

  // Flyer "video" export. The circles use animated GIFs, but html2canvas
  // rasterizes GIFs to a single frame, so capturing many frames doesn't
  // actually animate anything — it just makes the browser slow. Fast path:
  // snapshot the flyer ONCE at full quality, then compose a smooth clip
  // where the still flyer holds, and the big "Join a Class" call-out plus
  // QR code slide in during the final seconds. Takes 2-5 seconds instead
  // of several minutes.
  const [gifProgress, setGifProgress] = useState(0);
  const [mp4Progress, setMp4Progress] = useState(0);

  const classesUrl = (typeof window !== 'undefined' ? window.location.origin : 'https://casasamba.com') + '/classes';

  // Captures the flyer once and returns a canvas sized to (outW, outH).
  const snapshotFlyer = async (outW, outH) => {
    const src = await html2canvas(flyerRef.current, {
      backgroundColor: '#0a0a0a',
      scale: 1,
      useCORS: true,
      allowTaint: true,
      logging: false,
    });
    const base = document.createElement('canvas');
    base.width = outW;
    base.height = outH;
    const ctx = base.getContext('2d');
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(src, 0, 0, outW, outH);
    return base;
  };

  // Compose one output frame onto `dstCtx`. `t01` is 0..1 progress through
  // the clip; the call-out banner slides in during the last 35%.
  const composeFrame = (dstCtx, baseCanvas, dstW, dstH, t01, qrImg) => {
    dstCtx.fillStyle = '#0a0a0a';
    dstCtx.fillRect(0, 0, dstW, dstH);
    dstCtx.drawImage(baseCanvas, 0, 0);

    const bannerStart = 0.65;
    if (t01 < bannerStart) return;
    const bannerT = Math.min(1, (t01 - bannerStart) / (1 - bannerStart)); // 0..1 during banner
    const bannerH = Math.round(dstH * 0.18);
    const slideFrom = bannerH;
    const by = dstH - bannerH + slideFrom * (1 - bannerT); // slides up from below

    dstCtx.save();
    dstCtx.globalAlpha = bannerT;
    dstCtx.fillStyle = 'rgba(0,0,0,0.88)';
    dstCtx.fillRect(0, by, dstW, bannerH);
    dstCtx.strokeStyle = '#f4b400';
    dstCtx.lineWidth = 4;
    dstCtx.strokeRect(2, by + 2, dstW - 4, bannerH - 4);

    if (qrImg) {
      const qrSize = bannerH - 24;
      dstCtx.drawImage(qrImg, 16, by + 12, qrSize, qrSize);
      const tx = 16 + qrSize + 20;
      dstCtx.fillStyle = '#f4b400';
      dstCtx.font = `900 ${Math.round(bannerH * 0.22)}px system-ui, -apple-system, Arial`;
      dstCtx.textBaseline = 'top';
      dstCtx.fillText('JOIN A CLASS', tx, by + 14);
      dstCtx.fillStyle = '#ffffff';
      dstCtx.font = `bold ${Math.round(bannerH * 0.17)}px system-ui, -apple-system, Arial`;
      dstCtx.fillText('casasamba.com/classes', tx, by + 14 + Math.round(bannerH * 0.26));
      dstCtx.fillStyle = 'rgba(255,255,255,0.75)';
      dstCtx.font = `${Math.round(bannerH * 0.13)}px system-ui, -apple-system, Arial`;
      dstCtx.fillText('Scan the QR or tap the link in bio', tx, by + 14 + Math.round(bannerH * 0.52));
    }
    dstCtx.restore();
  };

  const buildQrImage = async () => {
    const dataUrl = await QRCode.toDataURL(classesUrl, { margin: 1, width: 240, color: { dark: '#000000', light: '#ffffff' } });
    const img = new Image();
    await new Promise((r) => { img.onload = r; img.src = dataUrl; });
    return img;
  };

  const exportGif = async () => {
    if (!flyerRef.current) return;
    setExporting(true);
    setGifProgress(5);
    try {
      const outW = 600;
      const outH = Math.round(outW * (1820 / 1300));
      const fps = 10;
      const seconds = 5;
      const totalFrames = fps * seconds;

      const base = await snapshotFlyer(outW, outH);
      setGifProgress(20);
      const qrImg = await buildQrImage();

      const gif = new GIF({
        workers: 2,
        quality: 10,
        width: outW,
        height: outH,
        workerScript: '/gif.worker.js',
        background: '#0a0a0a',
      });

      const outCanvas = document.createElement('canvas');
      outCanvas.width = outW;
      outCanvas.height = outH;
      const outCtx = outCanvas.getContext('2d');

      for (let i = 0; i < totalFrames; i++) {
        composeFrame(outCtx, base, outW, outH, i / (totalFrames - 1), qrImg);
        gif.addFrame(outCanvas, { copy: true, delay: 1000 / fps });
        setGifProgress(20 + Math.round(((i + 1) / totalFrames) * 30));
      }

      await new Promise((resolve, reject) => {
        gif.on('progress', (p) => setGifProgress(50 + Math.round(p * 50)));
        gif.on('finished', (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'casa-samba-classes-flyer.gif';
          a.click();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
          resolve();
        });
        gif.on('abort', () => reject(new Error('GIF render aborted')));
        gif.render();
      });

      toast.success('Flyer GIF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('GIF export failed — try PNG instead');
    } finally {
      setExporting(false);
      setGifProgress(0);
    }
  };

  const exportMp4 = async () => {
    if (!flyerRef.current) return;
    setExporting(true);
    setMp4Progress(5);
    try {
      const outW = 720;
      const outH = Math.round(outW * (1820 / 1300));
      const fps = 24;
      const seconds = 6;
      const totalFrames = fps * seconds;

      const base = await snapshotFlyer(outW, outH);
      setMp4Progress(20);
      const qrImg = await buildQrImage();

      const outCanvas = document.createElement('canvas');
      outCanvas.width = outW;
      outCanvas.height = outH;
      const outCtx = outCanvas.getContext('2d');

      const candidates = [
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4;codecs=avc1',
        'video/mp4',
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
      ];
      const mime = candidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m));
      if (!mime) throw new Error('Your browser cannot record video. Try Chrome or Safari.');

      const stream = outCanvas.captureStream(fps);
      const chunks = [];
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      const stopped = new Promise((resolve) => { rec.onstop = resolve; });

      // Prime canvas with first frame BEFORE starting the recorder so no
      // black frame leaks in.
      composeFrame(outCtx, base, outW, outH, 0, qrImg);
      rec.start();

      const frameMs = 1000 / fps;
      let i = 0;
      const start = performance.now();
      const tick = async () => {
        while (i < totalFrames) {
          const expected = start + i * frameMs;
          const now = performance.now();
          if (now < expected) {
            await new Promise((r) => setTimeout(r, expected - now));
          }
          composeFrame(outCtx, base, outW, outH, i / (totalFrames - 1), qrImg);
          setMp4Progress(20 + Math.round(((i + 1) / totalFrames) * 75));
          i++;
        }
      };
      await tick();

      // Give the recorder one more frame-interval worth of time to flush
      await new Promise((r) => setTimeout(r, frameMs * 2));
      rec.stop();
      await stopped;

      const blob = new Blob(chunks, { type: mime });
      const ext = mime.startsWith('video/mp4') ? 'mp4' : 'webm';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `casa-samba-classes-flyer.${ext}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setMp4Progress(100);
      toast.success(`Flyer video downloaded as .${ext}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Video export failed — try GIF instead');
    } finally {
      setExporting(false);
      setMp4Progress(0);
    }
  };

  // Group active classes by their exact time range so days that share a
  // time slot get listed together (e.g. TUE · THU · 7 PM) while different
  // times (SAT morning) show on their own row. Falls back to the old
  // one-liner if the /api/classes endpoint returns nothing.
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const shortDay = (d) => (d || '').slice(0, 3).toUpperCase();
  const scheduleGroups = (() => {
    if (!classes.length) return [];
    const byTime = new Map();
    classes.forEach((c) => {
      if (!c.day_of_week || !c.start_time) return;
      const key = `${c.start_time.trim()}||${(c.end_time || '').trim()}`;
      if (!byTime.has(key)) byTime.set(key, { start: c.start_time.trim(), end: (c.end_time || '').trim(), days: new Set() });
      byTime.get(key).days.add(c.day_of_week);
    });
    return [...byTime.values()]
      .map((g) => ({
        ...g,
        daysList: [...g.days].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b)),
      }))
      .sort((a, b) => dayOrder.indexOf(a.daysList[0]) - dayOrder.indexOf(b.daysList[0]));
  })();

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      <div className="max-w-[1360px] mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">
            <PageEditable path="page.title" as="span" fallback="Afro-Brazilian Culture Classes" />
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-sm">
            <PageEditable path="page.subtitle" as="span" multiline fallback="Samba · Afro-Brasilian · Orixa · Capoeira · Coco · Maracatu · Stilt. Download the flyer to share our schedule." />
          </p>
        </div>

        {/* WHEN / WHERE glance panel — top of page, right above the flyer */}
        <div
          className="flex flex-col sm:flex-row items-stretch mx-auto mb-6 rounded-full overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(90deg, #f4b400 0%, #ffcf3a 50%, #f4b400 100%)',
            border: '3px solid #000',
            maxWidth: 820,
            color: '#000',
            boxShadow: '0 14px 40px rgba(244,180,0,0.35), 0 4px 14px rgba(0,0,0,0.5)',
          }}
          data-testid="public-flyer-when-where"
        >
          <div className="flex-1 px-6 py-3.5 text-center">
            <div className="text-[11px] font-black tracking-[0.35em] uppercase">When</div>
            {scheduleGroups.length > 0 ? (
              <div className="mt-1 space-y-0.5" data-testid="public-flyer-schedule-list">
                {scheduleGroups.map((g, i) => (
                  <div key={i} className="text-sm sm:text-base font-black leading-tight">
                    <span>{g.daysList.map(shortDay).join(' · ')}</span>
                    <span className="mx-1.5 opacity-70">·</span>
                    <span>{g.start}{g.end ? ` – ${g.end}` : ''}</span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="text-xl sm:text-2xl font-black leading-tight mt-1">TUE · THU</div>
                <div className="text-sm font-bold">7:00 PM – 9:00 PM</div>
              </>
            )}
          </div>
          <div className="hidden sm:block w-[3px] bg-black my-2.5 rounded" />
          <div className="flex-1 px-6 py-3.5 text-center">
            <div className="text-[11px] font-black tracking-[0.35em] uppercase">Where</div>
            <div className="text-lg sm:text-xl font-black leading-tight mt-1">
              <PageEditable path="where.venue" as="span" fallback="N.O.R.D. Annunciation Center" />
            </div>
            <div className="text-sm font-bold">
              <PageEditable path="where.address" as="span" fallback="800 Race St · New Orleans, LA" />
            </div>
          </div>
        </div>

        {/* Flyer preview — full width, scrollable if smaller viewport.
            The whole flyer is also a tap target that opens /classes so
            viewers never need to scan the QR to reach the registration page. */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-white/60 py-16 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading...
            </div>
          ) : (
            <a
              href="/classes"
              className="group relative mx-auto block cursor-pointer"
              style={{ width: 1300 }}
              data-testid="flyer-clickable-wrap"
              aria-label="Tap to join a class"
            >
              <FlyerCircular ref={flyerRef} classes={classes} onClassesChanged={loadClasses} />
              <span className="pointer-events-none absolute inset-x-0 top-6 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="px-5 py-2 rounded-full bg-yellow-400 text-black font-black text-sm shadow-xl">
                  TAP TO JOIN A CLASS →
                </span>
              </span>
            </a>
          )}
        </div>

        {/* Primary call-to-action — always visible, always one tap away.
            Deliberately big and above the Download row so viewers who land
            here first want to JOIN before they want to download the flyer. */}
        <div className="flex justify-center mt-10">
          <a
            href="/classes"
            data-testid="flyer-join-cta"
            className="inline-flex items-center gap-3 px-12 py-5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-black text-xl shadow-2xl shadow-yellow-500/30 hover:scale-[1.02] transition-transform"
          >
            🥁 Join a Class — casasamba.com/classes
          </a>
        </div>

        {/* Download buttons — moved BELOW the flyer, spread out */}
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-16 mt-10">
          <button
            onClick={() => exportImage('png')}
            disabled={exporting || loading}
            data-testid="public-flyer-download-png"
            className="px-10 py-4 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-base flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            Download for Social (PNG)
          </button>
          <button
            onClick={() => exportImage('pdf')}
            disabled={exporting || loading}
            data-testid="public-flyer-download-pdf"
            className="px-10 py-4 rounded-xl bg-red-500 hover:bg-red-400 text-white font-bold text-base flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            Download to Print (PDF)
          </button>
          <button
            onClick={exportGif}
            disabled={exporting || loading}
            data-testid="public-flyer-download-gif"
            className="px-10 py-4 rounded-xl bg-purple-500 hover:bg-purple-400 text-white font-bold text-base flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Film className="w-5 h-5" />}
            {exporting && gifProgress > 0 ? `Making GIF... ${gifProgress}%` : 'Download Animated GIF'}
          </button>
          <button
            onClick={exportMp4}
            disabled={exporting || loading}
            data-testid="public-flyer-download-mp4"
            className="px-10 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-base flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
            {exporting && mp4Progress > 0 ? `Rendering video... ${mp4Progress}%` : 'Download Video (MP4)'}
          </button>
        </div>

        {/* Instagram how-to: three steps to make the flyer tap-to-join on IG.
            Deliberately plain-English, no jargon — the admin can read this
            whenever they share the flyer without having to ask again. */}
        <InstagramHowTo classesUrl={classesUrl} />
      </div>
    </div>
  );
};

const InstagramHowTo = ({ classesUrl }) => {
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(classesUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copy this link:', classesUrl);
    }
  };
  return (
    <div className="mt-16 max-w-4xl mx-auto bg-gradient-to-br from-pink-500/10 to-purple-500/10 border-2 border-pink-500/40 rounded-2xl p-6 sm:p-8" data-testid="instagram-howto">
      <div className="text-center mb-6">
        <div className="inline-block px-4 py-1.5 rounded-full bg-pink-500 text-white font-black text-xs tracking-[0.3em] uppercase mb-3">
          Posting to Instagram
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-white">
          Make this flyer tap-to-join on Instagram
        </h2>
        <p className="text-white/70 text-sm mt-2 max-w-xl mx-auto">
          Instagram doesn't let regular feed photos be clickable, but Stories do. Here are the three ways to turn one tap into a class signup.
        </p>
      </div>

      {/* Step cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="bg-black/40 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-7 h-7 rounded-full bg-pink-500 text-white font-black flex items-center justify-center text-sm">1</span>
            <h3 className="font-black text-white">Story + Link Sticker</h3>
          </div>
          <p className="text-sm text-white/80 leading-relaxed">
            Post the <strong>PNG</strong> to a Story. Tap the sticker icon → pick <strong>Link</strong> → paste the link below. Place the sticker on top of the flyer. One tap on the sticker = classes page.
          </p>
          <p className="text-xs text-white/50 mt-2">Save it to a Highlight called <em>Classes</em> so it never expires.</p>
        </div>

        <div className="bg-black/40 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-7 h-7 rounded-full bg-pink-500 text-white font-black flex items-center justify-center text-sm">2</span>
            <h3 className="font-black text-white">Feed post + Link in bio</h3>
          </div>
          <p className="text-sm text-white/80 leading-relaxed">
            Set your Instagram bio link to the link below. Post the flyer, and write <strong>"Link in bio → Join a class"</strong> in the caption. Two taps lands them on the classes page.
          </p>
          <p className="text-xs text-white/50 mt-2">Use a free "linktree"-style page if you need multiple links in bio.</p>
        </div>

        <div className="bg-black/40 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-7 h-7 rounded-full bg-pink-500 text-white font-black flex items-center justify-center text-sm">3</span>
            <h3 className="font-black text-white">Boost the post ($)</h3>
          </div>
          <p className="text-sm text-white/80 leading-relaxed">
            On a feed post tap <strong>Boost post</strong> → set destination to the link below. Instagram adds a real <em>Learn More</em> / <em>Book Now</em> button directly on the flyer. Costs $5+ to run.
          </p>
          <p className="text-xs text-white/50 mt-2">Only paid posts get a true one-tap button inside the feed.</p>
        </div>
      </div>

      {/* The link they need to paste */}
      <div className="mt-6 bg-black/60 rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 border border-pink-500/30">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black tracking-[0.25em] uppercase text-pink-400 mb-1">Paste this into Instagram</div>
          <div className="font-mono text-sm sm:text-base text-white break-all" data-testid="howto-link-text">{classesUrl}</div>
        </div>
        <button
          onClick={copyLink}
          data-testid="howto-copy-link"
          className={`px-6 py-3 rounded-full font-bold text-sm whitespace-nowrap transition-colors ${
            copied ? 'bg-green-500 text-black' : 'bg-pink-500 hover:bg-pink-400 text-white'
          }`}
        >
          {copied ? '✓ Copied!' : 'Copy link'}
        </button>
      </div>
    </div>
  );
};

export default FlyerPublic;
